import React, { useEffect, useState } from "react";
import { FiPlus, FiTrash2, FiSave, FiX } from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import JoditEditor from "jodit-react";

export default function AddMoreQuestionsModal({
  sectionId,
  onClose,
  nextQuestion,
  maxQuestions,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([
    {
      questionNo: "",
      questionType: "text",      // text | paragraph
      questionText: "",
      optionsType: "text",       // text | image
      options: { A: "", B: "", C: "", D: "" },
      correctOption: "",
    },
  ]);

  const handleAddQuestion = () => {
    setQuestions((prev) => {
      const lastQNo = prev[prev.length - 1].questionNo;

      if (lastQNo >= maxQuestions) {
        showErrorToast(
          `You cannot add more than ${maxQuestions} questions in this section`,
        );
        return prev;
      }

      return [
        ...prev,
        {
          questionNo: lastQNo + 1,
          questionType: "text",
          questionText: "",
          optionsType: "text",
          options: { A: "", B: "", C: "", D: "" },
          correctOption: "",
        },
      ];
    });
  };

  useEffect(() => {
    if (nextQuestion) {
      setQuestions([
        {
          questionNo: nextQuestion,
          questionType: "text",
          questionText: "",
          optionsType: "text",
          options: { A: "", B: "", C: "", D: "" },
          correctOption: "",
        },
      ]);
    }
  }, [nextQuestion]);

  const handleRemoveQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== index) return q;

        if (field.startsWith("options.")) {
          const key = field.split(".")[1];
          return {
            ...q,
            options: { ...q.options, [key]: value },
          };
        }

        return { ...q, [field]: value };
      }),
    );
  };

  const validateQuestions = () => {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];

      if (
        q.questionType === "text" &&
        !q.questionText.trim()
      ) {
        showErrorToast(`Question ${i + 1} text is required`);
        return false;
      }

      if (
        q.questionType === "paragraph" &&
        !q.questionText
      ) {
        showErrorToast(`Paragraph content required in Question ${i + 1}`);
        return false;
      }

      if (!q.correctOption) {
        showErrorToast(`Select correct option in Question ${i + 1}`);
        return false;
      }

      for (const key of ["A", "B", "C", "D"]) {
        if (!q.options[key]?.trim()) {
          showErrorToast(`Option ${key} missing in Question ${i + 1}`);
          return false;
        }
      }
    }
    return true;
  };


  const handleSubmit = async () => {
    if (!validateQuestions()) return;

    try {
      setLoading(true);
      const payload = {
        sectionId,
        questions: questions.map((q) => ({
          questionNo: q.questionNo,

          questionType: q.questionType, // text | paragraph

          questionText:
            q.questionType === "text"
              ? q.questionText.trim()
              : q.questionText, // paragraph → HTML allowed

          optionsType: q.optionsType, // text | image

          options: Object.fromEntries(
            Object.entries(q.options).map(([k, v]) => [k, v.trim()])
          ),

          correctOption: q.correctOption,
        })),
      };


      const res = await makeRequestAuth(
        "post",
        "/admin/addMoreQuestions",
        payload,
      );

      if (res?.error) {
        showErrorToast(res.message);
      } else {
        showSuccessToast("Questions added successfully!");
        onSuccess?.(); // Refresh the list
        onClose(); // Close modal
      }
    } catch (err) {
      showErrorToast(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col animate-in zoom-in-95 duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-50 p-2 bg-gray-50 text-gray-400 hover:text-red-500 rounded-xl transition-colors"
        >
          <FiX size={20} />
        </button>

        <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
          <div className="flex flex-col h-full">
            {/* 1. HEADER */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-blue-50 p-5 rounded-2xl border border-blue-100 mb-6 gap-4">
              <div>
                <h2 className="text-blue-700 text-lg font-bold">
                  Add New Questions
                </h2>
                <p className="text-xs text-blue-500 uppercase tracking-widest font-semibold mt-1">
                  Section ID: {sectionId.slice(-6)} • Total: {questions.length}
                </p>
              </div>
              <button
                onClick={handleAddQuestion}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 active:scale-95"
              >
                <FiPlus /> Add Question
              </button>
            </div>

            {/* 2. QUESTIONS LIST */}
            <div className="space-y-6 pb-10">
              {questions.map((q, idx) => (
                <div
                  key={idx}
                  className="group p-5 md:p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:border-blue-200 transition-all relative"
                >
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-gray-800 text-white rounded-lg flex items-center justify-center font-bold text-sm shadow-sm">
                        {q.questionNo}
                      </span>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Question Details
                      </span>
                    </div>

                    {questions.length > 1 && (
                      <button
                        onClick={() => handleRemoveQuestion(idx)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    )}
                  </div>

                  <select
                    value={q.questionType}
                    onChange={(e) =>
                      handleQuestionChange(idx, "questionType", e.target.value)
                    }
                    className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold bg-gray-50"
                  >
                    <option value="text">Text Question</option>
                    <option value="paragraph">Paragraph Question</option>
                  </select>


                  {/* Question Input */}
                  <div className="mb-4">
                    {q.questionType === "paragraph" ? (
                      <JoditEditor
                        value={q.questionText}
                        onChange={(val) =>
                          handleQuestionChange(idx, "questionText", val)
                        }
                      />
                    ) : (
                      <textarea
                        className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50/30"
                        placeholder="Enter your question text..."
                        rows={3}
                        value={q.questionText}
                        onChange={(e) =>
                          handleQuestionChange(idx, "questionText", e.target.value)
                        }
                      />
                    )}
                  </div>


                  <select
                    value={q.optionsType}
                    onChange={(e) =>
                      handleQuestionChange(idx, "optionsType", e.target.value)
                    }
                    className="mb-3 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold bg-gray-50"
                  >
                    <option value="text">Text Options</option>
                    <option value="image">Image (URL) Options</option>
                  </select>


                  {/* Options Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {["A", "B", "C", "D"].map((opt) => (
                      <div key={opt} className="relative group/opt">
                        {q.optionsType === "image" && q.options[opt] && (
                          <img
                            src={q.options[opt]}
                            alt={opt}
                            className="mt-2 max-h-[120px] rounded-lg border"
                          />
                        )}

                        <input
                          className={`w-full pl-12 pr-4 py-3 rounded-xl border transition-all outline-none text-sm ${q.correctOption === opt
                            ? "border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500 font-semibold text-emerald-900"
                            : "border-gray-200 focus:border-blue-400 text-gray-600"
                            }`}
                          placeholder={
                            q.optionsType === "image"
                              ? `Option ${opt} image URL`
                              : `Option ${opt} text`
                          }

                          value={q.options[opt]}
                          onChange={(e) =>
                            handleQuestionChange(
                              idx,
                              `options.${opt}`,
                              e.target.value,
                            )
                          }
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleQuestionChange(idx, "correctOption", opt)
                          }
                          className={`absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all ${q.correctOption === opt
                            ? "bg-emerald-500 text-white shadow-md"
                            : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                            }`}
                        >
                          {opt}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <button
                onClick={handleAddQuestion}
                className="w-full py-6 border-2 border-dashed border-gray-100 rounded-2xl text-gray-400 font-bold hover:border-blue-200 hover:text-blue-500 hover:bg-blue-50/50 transition-all flex items-center justify-center gap-2"
              >
                <FiPlus size={20} /> Add Another Question
              </button>
            </div>
          </div>
        </div>

        {/* 3. FOOTER */}
        <div className="p-6 md:p-8 bg-gray-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-8 py-3.5 text-gray-500 font-bold hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 disabled:opacity-50 active:scale-[0.98]"
          >
            {loading ? (
              "Saving..."
            ) : (
              <>
                <FiSave /> Save All Questions
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
